import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../../services/api/axios";

const fetchPartnerBalances = async ({ partner, currency }) => {
  const { data } = await api.get("/v1/admin/treasury/partner-balances", {
    params: {
      ...(partner && { partner }),
      ...(currency && { currency }),
    },
  });
  return data.data;
};

export const usePartnerBalances = (partner, currency, options = {}) => {
  return useQuery({
    queryKey: ["treasury-partner-balances", partner, currency],
    queryFn: () => fetchPartnerBalances({ partner, currency }),
    refetchOnWindowFocus: false,
    ...options,
  });
};

const fetchPartnerBalancesUsd = async () => {
  const { data } = await api.get("/v1/admin/treasury/partner-balances/usd-summary");
  return data.data;
};

// What partners hold, in USD: total, per partner and per row.
export const usePartnerBalancesUsd = () => {
  return useQuery({
    queryKey: ["treasury-partner-balances-usd"],
    queryFn: fetchPartnerBalancesUsd,
    refetchOnWindowFocus: false,
  });
};

const syncAllPartnerBalances = async () => {
  const { data } = await api.post("/v1/admin/treasury/partner-balances/sync");
  return data.data;
};

export const useSyncAllPartnerBalances = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncAllPartnerBalances,
    onSuccess: (outcomes) => {
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-balances"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-balances-usd"] });
      // The sync also recomputes the position on the server, so the gap
      // and suggestions need to be refetched too.
      queryClient.invalidateQueries({ queryKey: ["treasury-rebalance-suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-daily-position"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-liability-summary"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-held-breakdown"] });
      const failed = outcomes.filter((o) => !o.success);
      if (failed.length > 0) {
        toast.error(`Refreshed with ${failed.length} failure(s): ${failed.map((f) => f.partner).join(", ")}`);
      } else {
        toast.success("All partner balances refreshed");
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to refresh partner balances");
    },
  });
};

const syncOnePartnerBalance = async ({ partner, currency }) => {
  const { data } = await api.post(
    `/v1/admin/treasury/partner-balances/sync/${partner}`,
    null,
    { params: currency ? { currency } : undefined },
  );
  return data;
};

export const useSyncOnePartnerBalance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncOnePartnerBalance,
    onSuccess: ({ message, data }, { partner, currency }) => {
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-balances"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-balances-usd"] });
      // The sync also recomputes the position on the server, so the gap
      // and suggestions need to be refetched too.
      queryClient.invalidateQueries({ queryKey: ["treasury-rebalance-suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-daily-position"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-liability-summary"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-held-breakdown"] });
      if (currency && data.length === 0) {
        toast.error(message);
      } else {
        toast.success(currency ? `${partner} ${currency} balance refreshed` : `${partner} balance refreshed`);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to refresh balance");
    },
  });
};

const fetchHeldBreakdown = async (currency) => {
  const { data } = await api.get("/v1/admin/treasury/position/held-breakdown", {
    params: { currency },
  });
  return data.data;
};

export const useHeldBreakdown = (currency) => {
  return useQuery({
    queryKey: ["treasury-held-breakdown", currency],
    queryFn: () => fetchHeldBreakdown(currency),
    enabled: !!currency,
    refetchOnWindowFocus: false,
  });
};

const fetchDailyPosition = async (date) => {
  const { data } = await api.get("/v1/admin/treasury/position", {
    params: date ? { date } : undefined,
  });
  return data.data;
};

export const useDailyPosition = (date) => {
  return useQuery({
    queryKey: ["treasury-daily-position", date],
    queryFn: () => fetchDailyPosition(date),
    refetchOnWindowFocus: false,
  });
};

const fetchLiabilitySummary = async (date) => {
  const { data } = await api.get("/v1/admin/treasury/position/liability-summary", {
    params: date ? { date } : undefined,
  });
  return data.data;
};

// Total wallet liability in USD for a day's snapshot: merchant, house
// merchant (the collection wallet) and consumer.
export const useLiabilitySummary = (date) => {
  return useQuery({
    queryKey: ["treasury-liability-summary", date],
    queryFn: () => fetchLiabilitySummary(date),
    refetchOnWindowFocus: false,
  });
};

const runPositionCheck = async () => {
  const { data } = await api.post("/v1/admin/treasury/position/run");
  return data.data;
};

export const useRunPositionCheck = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runPositionCheck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-daily-position"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-liability-summary"] });
      toast.success("Position check complete");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to run position check");
    },
  });
};

// Conversion Log - three distinct views, kept as separate endpoints/hooks
// rather than one (see the treasury implementation plan's own reasoning):
// settlement-linked is merchant-only and the only one with a partner rate
// to compare against; merchant/consumer history are plain visibility over
// every conversion, regardless of whether it ever settled at a partner.
const fetchConversionLog = async ({ currency, from, to }) => {
  const { data } = await api.get("/v1/admin/treasury/conversion-log", {
    params: {
      ...(currency && { currency }),
      ...(from && { from }),
      ...(to && { to }),
    },
  });
  return data.data;
};

export const useConversionLog = ({ currency, from, to } = {}) => {
  return useQuery({
    queryKey: ["treasury-conversion-log", currency, from, to],
    queryFn: () => fetchConversionLog({ currency, from, to }),
    refetchOnWindowFocus: false,
  });
};

const fetchMerchantConversionHistory = async ({ currency, from, to }) => {
  const { data } = await api.get("/v1/admin/treasury/conversion-log/merchant", {
    params: {
      ...(currency && { currency }),
      ...(from && { from }),
      ...(to && { to }),
    },
  });
  return data.data;
};

export const useMerchantConversionHistory = ({ currency, from, to } = {}) => {
  return useQuery({
    queryKey: ["treasury-conversion-history-merchant", currency, from, to],
    queryFn: () => fetchMerchantConversionHistory({ currency, from, to }),
    refetchOnWindowFocus: false,
  });
};

const fetchConsumerConversionHistory = async ({ currency, from, to }) => {
  const { data } = await api.get("/v1/admin/treasury/conversion-log/consumer", {
    params: {
      ...(currency && { currency }),
      ...(from && { from }),
      ...(to && { to }),
    },
  });
  return data.data;
};

export const useConsumerConversionHistory = ({ currency, from, to } = {}) => {
  return useQuery({
    queryKey: ["treasury-conversion-history-consumer", currency, from, to],
    queryFn: () => fetchConsumerConversionHistory({ currency, from, to }),
    refetchOnWindowFocus: false,
  });
};

// Partner Conversion Console (Phase 2) - deliberately scoped to whichever
// partners the backend actually confirms support a real conversion
// endpoint (Korapay only, as of this writing) - never hardcode the list
// here, since guessing a second partner in would surface an option the
// backend will reject anyway.
const fetchSupportedConversionPartners = async () => {
  const { data } = await api.get("/v1/admin/treasury/partner-conversions/supported-partners");
  return data.data;
};

export const useSupportedConversionPartners = () => {
  return useQuery({
    queryKey: ["treasury-supported-conversion-partners"],
    queryFn: fetchSupportedConversionPartners,
    refetchOnWindowFocus: false,
  });
};

const quotePartnerConversion = async (payload) => {
  const { data } = await api.post("/v1/admin/treasury/partner-conversions/quote", payload);
  return data.data;
};

export const useQuotePartnerConversion = () => {
  return useMutation({ mutationFn: quotePartnerConversion });
};

const executePartnerConversion = async (payload) => {
  const { data } = await api.post("/v1/admin/treasury/partner-conversions/execute", payload);
  return data.data;
};

export const useExecutePartnerConversion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: executePartnerConversion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-conversion-history"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-balances"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-partner-balances-usd"] });
      // A conversion changes what partners hold, so refetch the gap too.
      queryClient.invalidateQueries({ queryKey: ["treasury-rebalance-suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-daily-position"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-liability-summary"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-held-breakdown"] });
    },
  });
};

const fetchPartnerConversionHistory = async (partner) => {
  const { data } = await api.get("/v1/admin/treasury/partner-conversions", {
    params: partner ? { partner } : undefined,
  });
  return data.data;
};

export const usePartnerConversionHistory = (partner) => {
  return useQuery({
    queryKey: ["treasury-partner-conversion-history", partner],
    queryFn: () => fetchPartnerConversionHistory(partner),
    refetchOnWindowFocus: false,
  });
};

// Orphaned consumer-withdrawal debit check - detection only, see the
// backend's OrphanedPayoutReconciliationService for why this never
// auto-fixes what it finds.
const runOrphanedPayoutCheck = async () => {
  const { data } = await api.post("/v1/admin/treasury/orphaned-payouts/check");
  return data.data;
};

export const useRunOrphanedPayoutCheck = () => {
  return useMutation({ mutationFn: runOrphanedPayoutCheck });
};

// Maker-checker for partner conversions above the USD threshold.
const fetchConversionApprovals = async (status) => {
  const { data } = await api.get("/v1/admin/treasury/partner-conversions/approvals", {
    params: status ? { status } : undefined,
  });
  return data.data;
};

export const useConversionApprovals = (status) => {
  return useQuery({
    queryKey: ["treasury-conversion-approvals", status],
    queryFn: () => fetchConversionApprovals(status),
    refetchOnWindowFocus: false,
  });
};

const requestConversionApproval = async (payload) => {
  const { data } = await api.post("/v1/admin/treasury/partner-conversions/approvals", payload);
  return data.data;
};

export const useRequestConversionApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestConversionApproval,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-conversion-approvals"] });
      toast.success("Approval requested - approvers have been emailed");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to request approval");
    },
  });
};

const decideConversionApproval = async ({ uuid, approve, note, code }) => {
  const { data } = await api.post(
    `/v1/admin/treasury/partner-conversions/approvals/${uuid}/${approve ? "approve" : "reject"}`,
    approve ? { note, code } : { note },
  );
  return data.data;
};

// Approving needs a one-time code emailed to the signed-in admin.
const requestApprovalCode = async (uuid) => {
  const { data } = await api.post(`/v1/admin/treasury/partner-conversions/approvals/${uuid}/code`);
  return data;
};

export const useRequestApprovalCode = () => {
  return useMutation({
    mutationFn: requestApprovalCode,
    onSuccess: () => toast.success("A code has been sent to your email"),
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to send the code");
    },
  });
};

export const useDecideConversionApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: decideConversionApproval,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["treasury-conversion-approvals"] });
      toast.success(variables.approve ? "Approved (valid for 24 hours, single use)" : "Declined");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to record decision");
    },
  });
};

const fetchThresholds = async () => {
  const { data } = await api.get("/v1/admin/treasury/thresholds");
  return data.data;
};

// { defaultBufferPct, overrides: [{ currency, minBufferPct }] }
export const useTreasuryThresholds = () => {
  return useQuery({
    queryKey: ["treasury-thresholds"],
    queryFn: fetchThresholds,
    refetchOnWindowFocus: false,
  });
};

const setThreshold = async ({ currency, minBufferPct }) => {
  const { data } = await api.put(`/v1/admin/treasury/thresholds/${currency}`, { minBufferPct });
  return data.data;
};

export const useSetTreasuryThreshold = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setThreshold,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-thresholds"] });
      toast.success("Buffer updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update buffer");
    },
  });
};

const fetchRebalanceSuggestions = async () => {
  const { data } = await api.get("/v1/admin/treasury/rebalance-suggestions");
  return data.data;
};

// { date, suggestions: [...], unresolved: [...], note? } - never executes
// anything; each suggestion links to the Partner Conversion console.
export const useRebalanceSuggestions = () => {
  return useQuery({
    queryKey: ["treasury-rebalance-suggestions"],
    queryFn: fetchRebalanceSuggestions,
    refetchOnWindowFocus: false,
    // A request that fails or hangs should not leave the page spinning.
    retry: 1,
  });
};

// Tracked expected top-ups: money sent to a partner that hasn't shown up in
// its synced balance yet. Display only - never counted as held.
const fetchTopUps = async () => {
  const { data } = await api.get("/v1/admin/treasury/topups");
  return data.data;
};

export const useTopUps = () => {
  return useQuery({
    queryKey: ["treasury-topups"],
    queryFn: fetchTopUps,
    refetchOnWindowFocus: false,
  });
};

const createTopUp = async (payload) => {
  const { data } = await api.post("/v1/admin/treasury/topups", payload);
  return data.data;
};

export const useCreateTopUp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTopUp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-topups"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-rebalance-suggestions"] });
      toast.success("Top-up recorded as in transit");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to record top-up");
    },
  });
};

const cancelTopUp = async (uuid) => {
  const { data } = await api.post(`/v1/admin/treasury/topups/${uuid}/cancel`);
  return data.data;
};

export const useCancelTopUp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelTopUp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-topups"] });
      queryClient.invalidateQueries({ queryKey: ["treasury-rebalance-suggestions"] });
      toast.success("Top-up cancelled");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to cancel top-up");
    },
  });
};

// Read-only: per currency, consumer owed vs house-merchant balance and how
// much of the gap comes from consumer movements that never reached the
// house merchant.
// `since` (ISO time, optional) limits every explanation column to movements
// from then on; the gap itself is always the live balance difference.
const fetchConsumerHouseReconciliation = async (since) => {
  const { data } = await api.get("/v1/admin/treasury/consumer-house-reconciliation", {
    params: since ? { since } : undefined,
  });
  return data.data;
};

export const useConsumerHouseReconciliation = (since, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ["treasury-consumer-house-reconciliation", since || null],
    queryFn: () => fetchConsumerHouseReconciliation(since),
    enabled,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// Deposits held for manual review, per currency, with what the house merchant
// already has for them and what approving them all would do to the gap.
const fetchConsumerHouseManualReview = async () => {
  const { data } = await api.get("/v1/admin/treasury/consumer-house-reconciliation/manual-review");
  return data.data;
};

export const useConsumerHouseManualReview = () => {
  return useQuery({
    queryKey: ["treasury-consumer-house-manual-review"],
    queryFn: fetchConsumerHouseManualReview,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// The individual records behind one figure of the reconciliation above (same
// conditions as the totals), newest first, capped by the API with the true
// total. Only fetched while its panel is open.
const fetchConsumerHouseReconciliationItems = async ({ kind, currency, since }) => {
  const { data } = await api.get("/v1/admin/treasury/consumer-house-reconciliation/items", {
    params: { kind, currency, ...(since && { since }) },
  });
  return data.data;
};

export const useConsumerHouseReconciliationItems = ({ kind, currency, since, enabled }) => {
  return useQuery({
    queryKey: ["treasury-consumer-house-reconciliation-items", kind, currency, since || null],
    queryFn: () => fetchConsumerHouseReconciliationItems({ kind, currency, since }),
    enabled: !!enabled && !!kind && !!currency,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// For one currency: every ledger movement on the consumer side and on the
// house-merchant wallets by type, and the same by day. The differences add up
// to the gap, so it shows where the two sides came apart.
const fetchConsumerHouseMoneyTrail = async ({ currency, days, from }) => {
  const { data } = await api.get("/v1/admin/treasury/consumer-house-reconciliation/money-trail", {
    params: { currency, ...(days && { days }), ...(from && { from }) },
  });
  return data.data;
};

export const useConsumerHouseMoneyTrail = ({ currency, days, from, enabled }) => {
  return useQuery({
    queryKey: ["treasury-consumer-house-money-trail", currency, days || null, from || null],
    queryFn: () => fetchConsumerHouseMoneyTrail({ currency, days, from }),
    placeholderData: (previous) => previous,
    enabled: !!enabled && !!currency,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// The ledger rows behind one line of the money trail, both sides.
const fetchConsumerHouseMoneyTrailRecords = async ({ currency, category, from }) => {
  const { data } = await api.get("/v1/admin/treasury/consumer-house-reconciliation/money-trail/records", {
    params: { currency, category, ...(from && { from }) },
  });
  return data.data;
};

export const useConsumerHouseMoneyTrailRecords = ({ currency, category, from, enabled }) => {
  return useQuery({
    queryKey: ["treasury-consumer-house-money-trail-records", currency, category, from || null],
    queryFn: () => fetchConsumerHouseMoneyTrailRecords({ currency, category, from }),
    enabled: !!enabled && !!currency && !!category,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// What partners charged us for payouts (their actual fee, stored on each
// payout) against what we charged to cover it, by provider and currency, plus
// the payouts where the partner cost more. The date range is optional.
const fetchFeeMargin = async ({ from, to }) => {
  const { data } = await api.get("/v1/admin/treasury/fee-margin", {
    params: { ...(from && { from }), ...(to && { to }) },
  });
  return data.data;
};

export const useFeeMargin = ({ from, to } = {}) => {
  return useQuery({
    queryKey: ["treasury-fee-margin", from, to],
    queryFn: () => fetchFeeMargin({ from, to }),
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// Mark payouts where the partner cost more than we charged as resolved once
// the fee settings were fixed (shows who did it and when), or undo that.
export const useResolveFeeMargin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ references, note }) => {
      const { data } = await api.post("/v1/admin/treasury/fee-margin/resolve", { references, note });
      return data.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["treasury-fee-margin"] });
      toast.success(`Marked ${result?.resolved ?? 0} payout(s) as resolved`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to mark as resolved");
    },
  });
};

export const useReopenFeeMargin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reference) => {
      const { data } = await api.post("/v1/admin/treasury/fee-margin/reopen", { reference });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["treasury-fee-margin"] });
      toast.success("Marked as outstanding again");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to undo");
    },
  });
};

const fetchPositionExplanation = async ({ currency, date, compareTo }) => {
  const { data } = await api.get("/v1/admin/treasury/position/explain", {
    params: { currency, date, ...(compareTo && { compareTo }) },
  });
  return data.data;
};

// Why a currency's Merchant to Partner position moved since the previous
// snapshot. Fetched only when asked for (enabled), since it runs several
// queries over the wallet transactions.
export const usePositionExplanation = ({ currency, date, compareTo, enabled }) => {
  return useQuery({
    queryKey: ["treasury-position-explain", currency, date, compareTo],
    queryFn: () => fetchPositionExplanation({ currency, date, compareTo }),
    enabled: !!currency && !!date && !!enabled,
    refetchOnWindowFocus: false,
  });
};
